import FJCanvasPool from '../FJCanvasPool';
import FJUtil from '../../util/FJUtil';
import FJSvgExporter from './FJSvgExporter';
import FJRenderState from './FJRenderState';
import $ from 'jquery';
import FJFontLoader from './FJFontLoader';
import FJCoreConfig from '../FJCoreConfig';
import FJTextStyled from '../FJTextStyled';
import FJMathUtil from '../../util/FJMathUtil';
import FJArchiveUtil from '../../util/FJArchiveUtil';
import FJDomUtil from '../../util/FJDomUtil';
import { isObject } from 'lodash-es';

const MAX_FRAME_CACHE = 5;
const MAX_RENDERING_CNT = 3;

class FJDomRender {
    /**
     * @desc
     * @param props
     */
    constructor(props) {
        this._element = props.element;
        this._id = this._element.getId();
        this._exportSize = props.exportSize;

        this._status = FJRenderState.IDLE;
        this._frames = [];
        this._watchers = [];
        this._cacheCnt = 0;
        this._frameIndex = 0; // 绑定帧索引
        this._renderingCnt = 0; // 渲染中的帧数，用于控制渲染并发数
        this._startedFrameCnt = 0; // 已开始渲染帧数
        this._renderedFrameCnt = 0; // 已渲染帧数
        this._totalFrameCnt = 0; // 总帧数
        this._errMsg = null;
        this._expandSize = 0; // 文字 \ 部分动画 渲染时，为了避免被裁剪，需要往外扩展一定的像素
        this._domSize = null; // dom的尺寸
        this._staticFrame = null; // 静态帧，元素只会存在唯一的静态帧，不可能存在多个不同的静态帧
        this._lastFrame = null; // 最后一帧, 避免计算误差造成的帧数不够的情况，用最后一帧补齐
    }

    get id() {
        return this._id;
    }

    get errMsg() {
        return this._errMsg;
    }

    get expandSize() {
        return this._expandSize;
    }

    get domSize() {
        return this._domSize;
    }

    /**
     * @desc 绑定帧索引
     * @return {number}
     */
    bindFrameIndex() {
        const idx = this._frameIndex;
        if (idx >= this._frames.length) {
            if (this._lastFrame) {
                // 存在最后一帧，则使用最后一帧补齐
                this._frames.push({
                    frame: this._lastFrame,
                    canRelease: false,
                });
                this._totalFrameCnt++;
            } else if (this._status === FJRenderState.RENDERING) {
                // 渲染中，需要补齐帧
                this._frames.push(null);
                this._totalFrameCnt++;
            } else {
                const lastFrame = this._frames[this._frameIndex.length - 1];
                if (lastFrame) {
                    this._frames.push({
                        frame: FJUtil.cloneCanvas(lastFrame.frame, null, true),
                        canRelease: true,
                    });
                    this._totalFrameCnt++;
                } else {
                    const msg = this._status === FJRenderState.ERROR ? this._errMsg : this._status;
                    const errMsg = `Element frame index out of range (${idx}/${this._frames.length}). Status: ${this._status}, error: ${msg}`;
                    this._error(errMsg);
                    return -1;
                }
            }
        }

        this._frameIndex++;
        return idx;
    }

    /**
     * @desc 观察某一帧的解码
     * @param idx
     * @param watcher
     */
    watchFrameAtIndex(idx, watcher) {
        this._watchers[idx] = watcher;
    }

    /**
     * @desc 帧渲染完成，响应观察者回调
     * @param idx
     * @param frame
     * @private
     */
    _addFrameAtIndex(idx, frame) {
        this._frames[idx] = frame;

        if (this._watchers[idx]) {
            this._watchers[idx]();
            this._watchers[idx] = null;
        }
    }

    /**
     * @desc 开始渲染
     * @param startTime
     * @param {object} options
     * @param {number | {horizontal: number, vertical: number}} options.expandRatio
     */
    start(startTime, options = {}) {
        this._status = FJRenderState.RENDERING;

        // 计算结束时间
        const timeData = this._element.getTimeData(true);
        const endTime = FJMathUtil.toFixed(timeData.duration - timeData.endDelay, 2);

        // 先使用null占位
        let time = startTime;
        const gap = FJCoreConfig.reFPS;
        while (FJMathUtil.toFixed(time, 2) <= endTime) {
            this._frames.push(null);
            time += gap;
        }
        this._totalFrameCnt = this._frames.length;

        // 加载所有元素渲染用到的字体

        const dom = this._element.getContentDom();
        if (!dom) {
            this._error('Element dom is null. type:' + this._element.getType());
            return;
        }

        this._loadFonts().then(fonts => {
            this._hasLineHeightZero = dom.style.cssText.indexOf('line-height: 0') >= 0;
            this._initDomSize(dom, options);
            const svgSize = {
                width: this._domSize.width + this._getExpandHorizontal() * 2,
                height: this._domSize.height + this._getExpandVertical() * 2,
            };
            this._svg = FJArchiveUtil.getSvgWithForeignObject(svgSize);

            let styles = $(this._svg).find('style')[0];
            $(styles).text('');
            fonts.forEach(({ fontName, bold, italic, base64 }) => {
                const textNode = document.createTextNode(
                    '' +
                        '* {box-sizing: border-box}' +
                        ' @font-face {font-family: "' +
                        fontName +
                        '";' +
                        'font-style:' +
                        (italic ? 'italic;' : 'normal;') +
                        'font-weight:' +
                        (bold ? '700;' : '400;') +
                        'src: url("data:application/font-woff;charset=utf-8;base64,' +
                        base64 +
                        '");}',
                );
                $(styles).append(textNode);
            });

            this._start(startTime);
        });
    }

    _getExpandHorizontal() {
        return isObject(this._expandSize) ? this._expandSize.horizontal : this._expandSize;
    }

    _getExpandVertical() {
        return isObject(this._expandSize) ? this._expandSize.vertical : this._expandSize;
    }

    /**
     * @desc 开始渲染
     * @param startTime, 故事版内的开始时间
     */
    _start(startTime) {
        // 创建svg输出
        this._exporter = new FJSvgExporter((image, errMsg, data) => {
            if (this._status === FJRenderState.ERROR) return;

            if (image) {
                // 如果是静态帧，则根据静态帧的数量，将静态帧的数据放到数组中
                if (data && data.isStatic) {
                    const cnt = data.staticFramesCnt;
                    for (let i = 0; i < cnt; i++) {
                        this._addFrameAtIndex(this._renderedFrameCnt, { frame: image, canRelease: false });
                        this._cacheCnt++;
                        this._renderedFrameCnt++;
                    }
                    // 标记静态帧，静态帧不由外部来释放，由内部clear时释放
                    this._staticFrame = image;
                } else {
                    this._addFrameAtIndex(this._renderedFrameCnt, { frame: image, canRelease: true });
                    this._cacheCnt++;
                    this._renderedFrameCnt++;
                }
                this._renderingCnt--;

                // 所有svg都渲染完毕才真正结束
                if (this._exporter.isEnd() && this._renderingCnt === 0) {
                    // 记录最后一帧
                    this._lastFrame = FJUtil.cloneCanvas(image, null, true);
                    // 使用最后一帧，补齐帧数
                    while (this._renderedFrameCnt < this._totalFrameCnt) {
                        this._addFrameAtIndex(this._renderedFrameCnt, {
                            frame: this._lastFrame,
                            canRelease: false,
                        });
                        this._renderedFrameCnt++;
                    }

                    this._done();
                } else {
                    this._draw();
                }
            } else {
                this._error(errMsg);
            }
        });
        this._exporter.start();

        this._nextTime = startTime;
        this._draw();
    }

    _draw() {
        const gap = FJCoreConfig.reFPS;

        // 必须满足两个条件才能渲染：
        // 1. 正在渲染的帧数小于最大渲染数
        // 2. 缓存中的帧数小于最大缓存数
        while (
            this._status === FJRenderState.RENDERING &&
            this._startedFrameCnt <= this._totalFrameCnt &&
            this._renderingCnt < MAX_RENDERING_CNT &&
            this._cacheCnt < MAX_FRAME_CACHE
        ) {
            // 渲染Dom
            this._element.renderInternalAtTime(this._nextTime);

            // 静态帧，则获取静态帧的数量
            const isStatic = this._element.isContentStaticAtTime(this._nextTime);
            let frameData = {
                isStatic: isStatic,
            };
            this._nextTime += gap;
            this._startedFrameCnt++;
            if (isStatic) {
                let cnt = 1;
                while (
                    this._startedFrameCnt <= this._totalFrameCnt &&
                    this._element.isContentStaticAtTime(this._nextTime)
                ) {
                    this._nextTime += gap;
                    this._startedFrameCnt++;
                    cnt++;
                }
                frameData.staticFramesCnt = cnt;
            }

            // dom转换为svg
            const dom = this._element.getContentDom();
            if (!dom) {
                this._error('Element dom is null. type:' + this._element.getType() + ', ' + this._element.getDomInfo());
                break;
            }
            this._domToSvg(dom, frameData);
        }

        // 判断所有dom已经全部开始了渲染
        if (this._startedFrameCnt >= this._totalFrameCnt) {
            // 注意这里的end，并不会将状态置为done，因为还有svgtoimage没有渲染完毕
            this._exporter.end();
        }
    }

    /**
     * @desc 将dom转换为svg
     * @param dom
     * @param frameData
     * @private
     */
    _domToSvg(dom, frameData) {
        if (!dom) {
            this._error('Element dom is null');
            return;
        }

        const parent = dom.parentNode;
        const domStyle = dom.style.cssText;
        try {
            dom.style.cssText =
                'position: absolute; top: ' +
                this._getExpandVertical() +
                'px; left: ' +
                this._getExpandHorizontal() +
                'px; width: ' +
                this._domSize.width +
                'px; height: ' +
                this._domSize.height +
                'px;' +
                (this._hasLineHeightZero ? ' line-height: 0;' : ''); // 某些情况下需要设置line-height:0，否则会导致canvas的错误
            // 将dom加入svg中
            const foreignObject = $(this._svg).find('foreignObject')[0];
            $(foreignObject).append(dom);

            // 序列号svg
            const svgStr =
                'data:image/svg+xml;charset=utf-8,' +
                encodeURIComponent(new XMLSerializer().serializeToString(this._svg));

            // 清空foreignObject
            $(foreignObject).empty();

            this._renderingCnt++;
            this._exporter.svgToImage(svgStr, frameData);
        } catch (err) {
            this._error(err.message);
        } finally {
            // 将dom还原
            dom.style.cssText = domStyle;
            parent.appendChild(dom);
        }
    }

    /**
     * @desc 初始化dom的大小
     * @param dom
     * @param {object} options
     * @param {number | {horizontal: number, vertical: number}} options.expandRatio
     * @private
     */
    _initDomSize(dom, options = {}) {
        if (!this._domSize) {
            let expandRatio = options?.expandRatio;

            // widget对象的尺寸可能不是dom的尺寸，而是其父节点的尺寸
            this._domSize = FJDomUtil.getDomSize(this._element.getType() === 'widget' ? dom.parentNode : dom);

            const maxWidth = this._exportSize?.width || 1920;
            const maxHeight = this._exportSize?.height || 1080;
            // 文字对象，需要扩大一定的范围
            if (this._element instanceof FJTextStyled) {
                expandRatio = options?.expandRatio || 1.5;

                const lineCnt = this._element.getTextLineCnt();
                if (isObject(expandRatio)) {
                    /**
                     * 目前只有部分动画的 expandRatio 是对象（参考 getExpandSizeRatio 接口），且 expandRatio 的尺寸倍数会比较大
                     * 导致在渲染时可能出现特别大的 canvas，比如 1920 * 5 = 9600，水平扩展为 9600，整个 canvas 的宽度为 9600 * 3 = 28800
                     * 这样的 canvas 达到一定数量后会导致 GPU 崩溃
                     * 所以需要在这种情况下限制 expandSize 的大小，以 exportSize 为准
                     *
                     * TODO 优化 expandSize 的计算逻辑，使其通用
                     *
                     */

                    this._expandSize = {
                        horizontal: Math.min(expandRatio.horizontal * this._domSize.width, maxWidth),
                        vertical: Math.min(expandRatio.vertical * Math.ceil(this._domSize.height / lineCnt), maxHeight),
                    };
                } else {
                    this._expandSize = expandRatio * Math.ceil(this._domSize.height / lineCnt);
                }

                const background = this._element.background;
                if (background && background.enabled && background.padding) {
                    const { left = 0, right = 0, top = 0, bottom = 0 } = background.padding;
                    if (isObject(this._expandSize)) {
                        this._expandSize = {
                            horizontal: Math.max(left, right, this._expandSize.horizontal),
                            vertical: Math.max(top, bottom, this._expandSize.vertical),
                        };
                    } else {
                        this._expandSize = Math.max(left, right, top, bottom, this._expandSize);
                    }
                }
            }
        }
    }

    /**
     * @desc 取消渲染
     */
    cancel() {
        this.clear();
    }

    /**
     * @desc 清空解码器
     */
    clear() {
        if (this._status === FJRenderState.CLEARED) return;

        this._status = FJRenderState.CLEARED;

        // element 停止输出
        if (this._element) {
            this._element.stopExport();
            this._element = null;
        }

        // 释放静态帧
        if (this._staticFrame) {
            FJCanvasPool.releaseCanvas(this._staticFrame);
            this._staticFrame = null;
        }

        // 释放最后一帧
        if (this._lastFrame) {
            FJCanvasPool.releaseCanvas(this._lastFrame);
            this._lastFrame = null;
        }

        // BUG：所传参数存在问题无法释放 释放所有缓存帧的canvas
        this._frames.forEach(frame => {
            FJCanvasPool.releaseCanvas(frame);
        });
        this._frames = [];

        // 释放bufferCanvas
        if (this._bufferCanvas) {
            FJCanvasPool.releaseCanvas(this._bufferCanvas);
            this._bufferCanvas = null;
        }

        // 释放svg渲染器
        if (this._exporter) {
            this._exporter.clear();
            this._exporter = null;
        }
    }

    /**
     * @desc 判断指定的帧是否准备好了
     * @param idx
     * @return {boolean}
     */
    framePreparedAtIndex(idx) {
        if (this._frames.length <= idx) return false;

        return !!this._frames[idx];
    }

    /**
     * @desc 获取指定帧的canvas
     * @param idx
     * @return {null|*}
     */
    getFrameAtIndex(idx) {
        if (this._frames.length <= idx) return null;

        const frame = this._frames[idx];
        this._frames[idx] = null;
        this._cacheCnt--;

        // 消耗一帧后，继续渲染
        this._draw();

        return frame;
    }

    /**
     * @desc 解码错误
     * @param msg
     * @private
     */
    _error(msg) {
        if (this._status !== FJRenderState.RENDERING) return;

        this.clear();

        this._status = FJRenderState.ERROR;
        this._errMsg = msg;

        const watcher = this._watchers.find(item => !!item);
        watcher && watcher(this._errMsg);
        this._watchers = [];
    }

    /**
     * @desc 解码完成
     * @private
     */
    _done() {
        if (this._status !== FJRenderState.RENDERING) return;

        this._status = FJRenderState.DONE;
    }

    /**
     * @desc 加载所有字体数据
     * @return {Promise<unknown[]>}
     * @private
     */
    _loadFonts() {
        // 获取所有字体的key
        const fontKeys = this._getFontKeys();

        // 加载字体
        let tasks = [];
        fontKeys.forEach(fontKey => {
            tasks.push(FJFontLoader.loadFontWithKey(fontKey));
        });

        return Promise.all(tasks);
    }

    /**
     * @desc 获取元素中的字体key
     * @return {*[]}
     * @private
     */
    _getFontKeys() {
        let fontKeys = [];
        if (this._element.getFontData) {
            const fontData = this._element.getFontData();
            if (fontData) {
                fontData.forEach(item => {
                    fontKeys.push(FJFontLoader.getFontKey(item));
                });
            }
        }
        // 去重
        fontKeys = [...new Set(fontKeys)];

        return fontKeys;
    }
}

export default FJDomRender;
